import { getEvmClient } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'
import { annualizeRateDeltaPercent } from '../annualizeRate'

/**
 * Wren `wstGBP` — intrinsic APR on a NAV the issuer restates weekly.
 *
 * wstGBP is a value-accruing wrapper over `tGBP`, the FCA-registered GBP
 * stablecoin issued by BCP Technologies (cash + short-dated gilts, 1:1
 * redeemable). A unit of wstGBP redeems for more tGBP over time as
 * `navprice()` is restated.
 *
 * **Primary: Wren's own `/api/nav-growth`.** It returns `aprWad` — a WAD
 * FRACTION (`34652393679138917` = 3.4652 %), so `/1e16` for the package's
 * percent convention — alongside the `windowDays`/`sampleCount` it used and
 * the full `history` of `{price, timestamp}` restatements. Verified
 * 2026-08-14 that `aprWad` reproduces exactly from the first and last history
 * points: `(1.008720286/1.005372972 − 1) × yearSecs/3_030_000 = 3.4646 %`.
 *
 * **Fallback: the same delta read on-chain**, and it is deliberately the
 * fallback, because it is BIASED HIGH and cannot easily not be. `navprice()`
 * is a STEP function — restated roughly weekly, flat in between — so sampling
 * it at a block ~30 days back and dividing by the elapsed time *between the
 * blocks* attributes the whole step to a window that is shorter than the one
 * the price actually spanned. Measured on 2026-08-14 that reads **4.04 %
 * against the API's 3.47 %, a 17 % overstatement**, purely from the ~5.7 days
 * between the sampled block and the restatement it inherited. The API divides
 * by the restatement timestamps instead, which is correct.
 *
 * The fallback therefore uses a **90-day** window rather than 30: the error is
 * bounded by one restatement interval, so lengthening the window shrinks it
 * proportionally (≈7/90 instead of ≈7/30). It still runs high — treat it as a
 * ceiling, and prefer the API whenever it answers.
 *
 * The on-chain leg needs an archival Ethereum RPC (six of the ten configured
 * chain-1 endpoints served 30-day-old state when this was written). Both legs
 * are sanity-checked — rates ≥ 1e18 and non-decreasing, since the token
 * launched at exactly par and only accrues — and the fetcher rotates
 * endpoints and throws rather than emit a bogus number.
 *
 * NB the APR is measured on `navprice()`, NOT on `burncost()` — the redeemable
 * price carries a ~25 bps exit haircut that is a one-off cost of leaving, not
 * a drag on the accrual. The reader reports that gap as
 * `redemptionDiscountBps`.
 *
 * DefiLlama has no `wren` protocol or pool (checked 2026-08-14; every GBP pool
 * it lists is a DEX pair or the Morpho `BBQTGBP` vault), so there is no third
 * source to fall back to.
 */

const CHAIN_ID = '1'
const WSTGBP: Address = '0x57c3571f10767e49c9d7b60feb6c67804783b7ae'

const WREN_NAV_GROWTH_URL = 'https://wstgbp.com/api/nav-growth'

/** 1e18 — wstGBP, tGBP and `navprice()` are all 18-decimal. */
const ONE_E18 = 10n ** 18n

/** Fallback lookback. Longer than the API's 30 days on purpose — see the
 *  step-function bias in the docstring above. */
const WINDOW_SECONDS = 90 * 24 * 60 * 60
/** Ethereum block time ~12 s. The elapsed time is read back from the block
 *  timestamps, so drift here only shifts which block is sampled. */
const BLOCK_TIME_SECONDS = 12
const WINDOW_BLOCKS = BigInt(Math.floor(WINDOW_SECONDS / BLOCK_TIME_SECONDS))

/**
 * Configured chain-1 endpoints to try before giving up on archive state.
 *
 * Deliberately SMALL. This is a fallback on a path the savings fetch awaits,
 * and every extra rotation is four more contended round-trips: at six tries it
 * could block `fetchSavingsVaults` for over a minute whenever the API was
 * slow, which is a latency bug for every caller and not just for tests. Six of
 * the ten configured endpoints served 30-day-old state when this was written,
 * so two tries is ample.
 */
const MAX_RPC_TRIES = 2

/** Hard ceiling on the whole fallback, for the same reason. Past this the
 *  right answer is no number at all, not a late one. */
const ONCHAIN_DEADLINE_MS = 12_000

export const WSTGBP_KEY = 'WSTGBP'
/** Token-list assetGroup (Ethereum, the only deployment). */
export const WSTGBP_GROUP_KEY = 'Wren Staked tGBP::wstGBP'

const NAVPRICE_ABI = [
  {
    name: 'navprice',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

/** Wren's published growth figure. Returns a PERCENT APR. */
const fetchWrenAprPercent = async (): Promise<number> => {
  const res = await fetch(WREN_NAV_GROWTH_URL, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(8000),
  })
  if (!res.ok) throw new Error(`Wren HTTP ${res.status}`)
  const json = (await res.json()) as { aprWad?: string | number }
  // WAD fraction → percent. `Number` on the raw string is safe: a plausible
  // APR is ~1e16-1e17, far inside the float-exact integer range.
  const apr = Number(json?.aprWad) / 1e16
  if (!Number.isFinite(apr) || apr <= 0) throw new Error('aprWad missing')
  return apr
}

const readNavAt = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: any,
  blockNumber?: bigint,
): Promise<bigint> =>
  client.readContract({
    address: WSTGBP,
    abi: NAVPRICE_ABI,
    functionName: 'navprice',
    ...(blockNumber !== undefined ? { blockNumber } : {}),
  }) as Promise<bigint>

/** On-chain rate-delta. Returns a PERCENT APR, biased HIGH — see above. */
const computeAprOnChain = async (): Promise<number> => {
  let lastErr: unknown
  const deadline = Date.now() + ONCHAIN_DEADLINE_MS
  for (let rpcId = 0; rpcId < MAX_RPC_TRIES; rpcId++) {
    if (Date.now() > deadline) break
    try {
      const client = getEvmClient(CHAIN_ID, rpcId)
      const head = await client.getBlockNumber()
      const pastBlock = head > WINDOW_BLOCKS ? head - WINDOW_BLOCKS : 0n

      const [navNow, navThen, headBlock, thenBlock] = await Promise.all([
        readNavAt(client),
        readNavAt(client, pastBlock),
        client.getBlock({ blockNumber: head }),
        client.getBlock({ blockNumber: pastBlock }),
      ])

      // A pruned node answers old state as 0 or reverts. Both legs must be a
      // real NAV — the token launched at exactly 1e18 and only accrues, so
      // anything below par is a bad read, never a loss.
      if (navNow < ONE_E18 || navThen < ONE_E18) {
        throw new Error('wstGBP: navprice below par — pruned or bad read')
      }
      if (navNow < navThen) {
        throw new Error('wstGBP: navprice decreased — non-archival RPC')
      }

      const elapsed = Number(headBlock.timestamp - thenBlock.timestamp)
      if (elapsed <= 0) throw new Error('wstGBP: non-positive window')

      return annualizeRateDeltaPercent(navNow, navThen, elapsed)
    } catch (e) {
      lastErr = e
    }
  }
  throw lastErr ?? new Error('wstGBP: no RPC served archive state')
}

export const wrenFetcher: YieldFetcher = {
  label: 'WREN',
  fetch: async () => {
    const apr = await fetchWrenAprPercent()
      .catch(() => computeAprOnChain())
      .catch(() => 0)
    return { [WSTGBP_KEY]: apr, [WSTGBP_GROUP_KEY]: apr }
  },
}
